/**
 * Terminal authentication outcomes (P1-12; server side:
 * backend/src/common/middleware/error-handler.ts TERMINAL_AUTH_CODES).
 *
 * The bridge retries 401/403/429/5xx: a cell dropout or a server blip must
 * never cost a responder their session or their queued work. These codes are
 * the exception. They say the credential is dead for good:
 *
 *   SESSION_REVOKED     this device session was revoked (lost phone, admin, logout elsewhere in its lineage)
 *   MEMBERSHIP_REMOVED  the user is no longer a member of the tenant
 *   USER_DISABLED       the account was disabled
 *   TOKEN_REVOKED       the token predates a jwt_version bump, or the user is gone
 *   DEVICE_REPORTED_LOST this device was reported lost or stolen from the console
 *                       (migration 582): withdrawn like SESSION_REVOKED, and the
 *                       sign-in screen says why
 *
 * SESSION_REVOKED, MEMBERSHIP_REMOVED and USER_DISABLED mean access was
 * WITHDRAWN: the app signs out (reason 'revoked') and wipes the device's
 * offline field data (the Dexie database and the tracking session state). The
 * data must not stay readable on a device whose access was withdrawn.
 *
 * MEMBERSHIP_REMOVED withdraws ONE tenant. When the token still claims other
 * memberships (a multi-tenant user removed from the active tenant only), the
 * app still signs out, but wipes only that tenant's offline records (every
 * Dexie table with a tenant_id index) and keeps the other tenants' unsynced
 * work. Records without a tenant tag (the GPS position buffer) are kept; points
 * of a session the user can no longer see are dead-lettered by the ingest
 * client (field-ingest.service.ts, parent_pending bound), never sent elsewhere.
 * With no other membership it is the full wipe above.
 *
 * TOKEN_REVOKED means only that this token is stale (the same person changed
 * their password or signed out everywhere): the app signs out (reason
 * 'expired', so the device keeps its push registration) but keeps the offline
 * data, which the same person uploads after signing in again. Wiping there
 * would destroy a responder's unsynced work because they changed a password.
 */
import { decodeJwtPayload, useAuthStore } from '@/store/auth.store';
import { rowTenantsOf } from '@/offline/row-tenant';
import { useTenantStore } from '@/store/tenant.store';

export const TERMINAL_AUTH_CODES = [
  'SESSION_REVOKED',
  'MEMBERSHIP_REMOVED',
  'USER_DISABLED',
  'TOKEN_REVOKED',
  'DEVICE_REPORTED_LOST',
] as const;
export type TerminalAuthCode = (typeof TERMINAL_AUTH_CODES)[number];

const TERMINAL_SET: ReadonlySet<string> = new Set(TERMINAL_AUTH_CODES);

/** Codes that withdraw access: sign out AND wipe offline field data. */
export const ACCESS_WITHDRAWN_CODES: ReadonlySet<TerminalAuthCode> = new Set<TerminalAuthCode>([
  'SESSION_REVOKED',
  'MEMBERSHIP_REMOVED',
  'USER_DISABLED',
  'DEVICE_REPORTED_LOST',
]);

/**
 * The terminal code an error response carries, or null. The server marks each
 * one with `error.terminal: true`; a known code is accepted without the flag
 * too, so an older server build is still understood.
 */
export function terminalAuthCode(status: number | undefined, data: unknown): TerminalAuthCode | null {
  if (status !== 401 && status !== 403) return null;
  if (!data || typeof data !== 'object') return null;
  const error = (data as { error?: { code?: unknown; terminal?: unknown } }).error;
  if (!error || typeof error !== 'object') return null;
  const code = typeof error.code === 'string' ? error.code : null;
  if (code && TERMINAL_SET.has(code)) return code as TerminalAuthCode;
  // Flagged terminal with a code this build does not know yet: still terminal.
  if (error.terminal === true) return 'TOKEN_REVOKED';
  return null;
}

/** Same check for an axios-style error object. */
export function terminalAuthCodeFromError(error: unknown): TerminalAuthCode | null {
  if (!error || typeof error !== 'object') return null;
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  return terminalAuthCode(response?.status, response?.data);
}

type TerminalListener = (code: TerminalAuthCode) => void;
const listeners = new Set<TerminalListener>();

/** Other services drop their own credential caches (tracker token) here. */
export function onTerminalAuth(listener: TerminalListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * DEVICE_REPORTED_LOST only: delete the device's offline field data outright, the whole Dexie
 * database (position buffer, queued mutations, cached and drafted field records, the safety
 * outbox) and the persisted tracking-session state. A lost device must not keep anyone's data,
 * so ANY undelivered entry on it, including another user's pending work and an undelivered
 * safety entry, is lost in that case (documented; every other reason uses wipeUserFieldData).
 * Never throws.
 */
export async function wipeFieldData(): Promise<void> {
  try {
    const { usePersonnelTrackingStore } = await import('@/store/personnel-tracking.store');
    await usePersonnelTrackingStore.getState().reset();
  } catch {
    // best effort: the store may not have loaded
  }
  try {
    // Imported lazily: the API client loads this module, and must not pull
    // the whole offline schema into every bundle and test that uses it.
    const { db } = await import('@/offline/db');
    // Deleting the whole database fires no table hooks: void the drain receipt first, so a
    // receipt never describes a handoff of data this wipe just removed (the next drain cycle
    // snapshots what is left and writes a new one).
    const { invalidateReceiptNow } = await import('@/offline/drain/drain-write-tracker');
    await invalidateReceiptNow().catch(() => undefined);
    await db.delete();
    // Re-open so code still holding `db` until the redirect lands does not throw.
    await db.open();
  } catch (err) {
    console.warn('[auth] offline data wipe failed', err);
  }
}

/**
 * Tables a user / tenant wipe never deletes from: a life-safety record is never deleted on the
 * device. The safety lane sends an entry only under its own originator and tenant
 * (safety-outbox.service.ts isOwnedBy), so an entry nobody here may send stays unsent and the
 * drain hands it off (the server stores a raising kind quarantined in its own tenant).
 */
export const TENANT_WIPE_KEEPS: ReadonlySet<string> = new Set(['safetyOutbox']);

/** Does this stored row belong to the tenant (row-tenant.ts: the same reading as the send gate)? */
export function rowBelongsToTenant(row: unknown, tenantId: string): boolean {
  return rowTenantsOf(row).includes(tenantId);
}

export interface WipeScope {
  /** The user whose access ended: their own rows go. */
  userId: string | null;
  /** Only rows of this tenant (MEMBERSHIP_REMOVED while a member elsewhere); null = every tenant. */
  tenantId: string | null;
}

/**
 * Does a user / tenant wipe delete this row? Within the scope's tenant (or every tenant):
 *  - a cache row (a server copy: nothing pending) goes;
 *  - pending work goes only when it is the affected user's own (its originator);
 *  - another originator's pending work, an unknown originator's, and unstamped pending rows of
 *    older builds stay, for their own sync or the drain's handoff.
 * TENANT_WIPE_KEEPS tables are never touched.
 */
export function wipeDeletes(
  tableName: string,
  row: Record<string, unknown>,
  scope: WipeScope,
  isOwed: (table: string, row: Record<string, unknown>) => boolean,
  originatorOf: (table: string, row: Record<string, unknown>) => string | null
): boolean {
  if (TENANT_WIPE_KEEPS.has(tableName)) return false;
  if (scope.tenantId && !rowBelongsToTenant(row, scope.tenantId)) return false;
  if (!isOwed(tableName, row)) return true;
  return !!scope.userId && originatorOf(tableName, row) === scope.userId;
}

/**
 * Delete what a user whose access ended (USER_DISABLED, SESSION_REVOKED, MEMBERSHIP_REMOVED)
 * leaves on a SHARED device: their own rows and the cached server copies, in every Dexie table
 * (or in one tenant), and the tracking-session state. Other people's pending work and
 * undelivered safety entries stay (wipeDeletes). Never throws.
 */
export async function wipeUserFieldData(scope: WipeScope): Promise<void> {
  try {
    const { usePersonnelTrackingStore } = await import('@/store/personnel-tracking.store');
    await usePersonnelTrackingStore.getState().reset();
  } catch {
    // best effort
  }
  try {
    const { db } = await import('@/offline/db');
    const { invalidateReceiptNow, isOwedRow, originatorOfRow } = await import('@/offline/drain/drain-write-tracker');
    await invalidateReceiptNow().catch(() => undefined);
    for (const table of db.tables) {
      if (TENANT_WIPE_KEEPS.has(table.name)) continue;
      try {
        await table.filter((row) => wipeDeletes(table.name, row as Record<string, unknown>, scope, isOwedRow, originatorOfRow)).delete();
      } catch (err) {
        console.warn(`[auth] offline wipe of ${table.name} failed`, err);
      }
    }
  } catch (err) {
    console.warn('[auth] offline data wipe failed', err);
  }
}

/** MEMBERSHIP_REMOVED while still a member elsewhere: the removed user's rows of that tenant, and its cache. */
export function wipeTenantFieldData(tenantId: string, userId: string | null = null): Promise<void> {
  return wipeUserFieldData({ userId, tenantId });
}

/** Does this token still claim a membership in a tenant other than `tenantId`? */
export function tokenClaimsOtherTenant(token: string | null, tenantId: string): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  const memberships = Array.isArray(payload?.memberships) ? payload.memberships : [];
  return memberships.some(
    (m) =>
      !!m &&
      typeof m === 'object' &&
      typeof (m as { tenant_id?: unknown }).tenant_id === 'string' &&
      (m as { tenant_id: string }).tenant_id !== tenantId
  );
}

let inFlight: Promise<void> | null = null;

export interface TerminalAuthOptions {
  /** Send the browser to /login afterwards (default true). */
  redirect?: boolean;
  /** The tenant a MEMBERSHIP_REMOVED answer was for (default: the active tenant). */
  tenantId?: string | null;
  /** For tests. */
  wipe?: () => Promise<void>;
  /** For tests. */
  wipeTenant?: (tenantId: string, userId: string | null) => Promise<void>;
  /** For tests. */
  wipeUser?: (scope: WipeScope) => Promise<void>;
}

/**
 * Sign out for good and wipe field data. Single-flight: a burst of requests
 * failing with the same terminal answer does the work once.
 */
export function handleTerminalAuth(code: TerminalAuthCode, options: TerminalAuthOptions = {}): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    for (const listener of listeners) {
      try {
        listener(code);
      } catch {
        // a listener's failure must not stop the sign-out
      }
    }
    const withdrawn = ACCESS_WITHDRAWN_CODES.has(code);
    const auth = useAuthStore.getState();
    // Whose access ended: read before logout() clears it.
    const affectedUser = auth.user?.id ?? null;
    // Read before logout() clears the token.
    const removedTenant =
      code === 'MEMBERSHIP_REMOVED' ? (options.tenantId ?? useTenantStore.getState().activeTenant?.id ?? null) : null;
    const tenantOnly = removedTenant !== null && tokenClaimsOtherTenant(auth.token, removedTenant);
    if (auth.isAuthenticated || auth.token) auth.logout(withdrawn ? 'revoked' : 'expired');
    if (withdrawn && tenantOnly && removedTenant) {
      await (options.wipeTenant ?? wipeTenantFieldData)(removedTenant, affectedUser);
      // Never sign back in pointed at the tenant that removed this user.
      if (useTenantStore.getState().activeTenant?.id === removedTenant) useTenantStore.getState().clearTenants();
    } else if (withdrawn && code === 'DEVICE_REPORTED_LOST') {
      // A lost device keeps nobody's data (undelivered entries on it are lost: see wipeFieldData).
      await (options.wipe ?? wipeFieldData)();
    } else if (withdrawn) {
      // The user's own data and the caches go; other people's pending work and undelivered
      // safety entries on this shared device stay for their own sync or the handoff.
      await (options.wipeUser ?? wipeUserFieldData)({ userId: affectedUser, tenantId: null });
    }
    if (options.redirect !== false && typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = withdrawn
        ? `/login?reason=revoked&code=${encodeURIComponent(code)}`
        : '/login?reason=expired';
    }
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
